import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Users2, Phone, Mail, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const CONDICIONES_IVA = [
  { value: 'responsable_inscripto', label: 'Resp. Inscripto' },
  { value: 'monotributo',           label: 'Monotributo'      },
  { value: 'exento',                label: 'Exento'           },
  { value: 'consumidor_final',      label: 'Consumidor Final' },
  { value: 'no_aplica',             label: 'No aplica'        },
];

const emptyForm = {
  razon_social: '', nombre_fantasia: '', cuit: '', condicion_iva: 'consumidor_final',
  telefono: '', email: '', direccion: '', localidad: '', provincia: '', notas: '',
};

function ClienteForm({ form, onChange }) {
  const set = (f) => (e) => onChange({ ...form, [f]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Razón social *</label>
          <input className="input" value={form.razon_social} onChange={set('razon_social')} placeholder="Ej: Juan Pérez" />
        </div>
        <div>
          <label>Nombre fantasía</label>
          <input className="input" value={form.nombre_fantasia} onChange={set('nombre_fantasia')} placeholder="Opcional" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>CUIT / DNI</label>
          <input className="input" value={form.cuit} onChange={set('cuit')} placeholder="20-12345678-9" />
        </div>
        <div>
          <label>Condición IVA</label>
          <select className="input" value={form.condicion_iva} onChange={set('condicion_iva')}>
            {CONDICIONES_IVA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Teléfono</label>
          <input className="input" type="tel" value={form.telefono} onChange={set('telefono')} placeholder="11 1234-5678" />
        </div>
        <div>
          <label>Email</label>
          <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="cliente@email.com" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Dirección</label>
          <input className="input" value={form.direccion} onChange={set('direccion')} placeholder="Calle y número" />
        </div>
        <div>
          <label>Localidad</label>
          <input className="input" value={form.localidad} onChange={set('localidad')} placeholder="Ciudad" />
        </div>
      </div>

      <div>
        <label>Notas</label>
        <textarea className="input resize-none" rows={2} value={form.notas} onChange={set('notas')} placeholder="Observaciones, descuentos habituales, etc." />
      </div>
    </div>
  );
}

const IVA_LABEL = {
  responsable_inscripto: 'Resp. Inscripto',
  monotributo:           'Monotributo',
  exento:                'Exento',
  consumidor_final:      'Cons. Final',
  no_aplica:             'No aplica',
};

export default function Clientes() {
  const { can } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const puedeGestionar = can('clientes.gestionar');

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await api.get('/clientes/index.php', { params });
      setClientes(res.data);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit   = (c) => {
    setEditItem(c);
    setForm({
      razon_social:    c.razon_social    || '',
      nombre_fantasia: c.nombre_fantasia || '',
      cuit:            c.cuit            || '',
      condicion_iva:   c.condicion_iva   || 'consumidor_final',
      telefono:        c.telefono        || '',
      email:           c.email           || '',
      direccion:       c.direccion       || '',
      localidad:       c.localidad       || '',
      provincia:       c.provincia       || '',
      notas:           c.notas           || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.razon_social.trim()) { toast.error('La razón social es requerida'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/clientes/index.php?id=${editItem.id}`, form);
        toast.success('Cliente actualizado');
      } else {
        await api.post('/clientes/index.php', form);
        toast.success('Cliente creado');
      }
      setModalOpen(false);
      fetchClientes();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clientes/index.php?id=${confirmId}`);
      toast.success('Cliente eliminado');
      setConfirmId(null);
      fetchClientes();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Clientes</h1>
        {puedeGestionar && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo cliente
          </button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="Buscar por nombre, CUIT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300">
            <Users2 size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">No hay clientes{search ? ' con ese criterio' : ''}</p>
            {puedeGestionar && !search && (
              <button className="btn-primary mt-4" onClick={openCreate}><Plus size={16} /> Crear el primero</button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>CUIT / DNI</th>
                  <th>Condición IVA</th>
                  <th>Contacto</th>
                  <th>Localidad</th>
                  {puedeGestionar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {clientes.map(c => (
                  <tr key={c.id}>
                    <td>
                      <p className="font-medium text-slate-900">{c.razon_social}</p>
                      {c.nombre_fantasia && <p className="text-xs text-slate-400">{c.nombre_fantasia}</p>}
                    </td>
                    <td className="text-slate-500 font-mono text-xs">{c.cuit || <span className="text-slate-300">—</span>}</td>
                    <td>
                      <span className="badge-neutral text-xs">{IVA_LABEL[c.condicion_iva] ?? c.condicion_iva}</span>
                    </td>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        {c.telefono && (
                          <a href={`tel:${c.telefono}`} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600 transition-colors">
                            <Phone size={11} /> {c.telefono}
                          </a>
                        )}
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600 transition-colors">
                            <Mail size={11} /> {c.email}
                          </a>
                        )}
                        {!c.telefono && !c.email && <span className="text-slate-300 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="text-slate-500 text-sm">{c.localidad || <span className="text-slate-300">—</span>}</td>
                    {puedeGestionar && (
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => setConfirmId(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? 'Editar cliente' : 'Nuevo cliente'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editItem ? 'Guardar cambios' : 'Crear cliente'}
            </button>
          </>
        }
      >
        <ClienteForm form={form} onChange={setForm} />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar cliente"
        message="¿Seguro que querés eliminar este cliente? Las facturas asociadas no se verán afectadas."
      />
    </div>
  );
}
